import React from 'react';
import GlossaryTerm from './components/GlossaryTerm';
import HelpBadge from './components/HelpBadge';
import './CompliDeskWorkspace.css';

type DeskTicket = {
  id?: number;
  number?: string;
  title?: string;
  status?: string;
  type?: string;
  priority?: string;
  created_at?: string;
  updated_at?: string;
  detail?: string;
  source?: string;
  signature?: string;
  notes?: Array<{ body?: string; at?: string }>;
  sla?: { state?: string };
};

type DeskService = { id?: number; name?: string; description?: string };
type DeskArticle = { id?: number; title?: string; body?: string; created_at?: string };

type BoardPayload = {
  counts?: {
    open?: number;
    breached?: number;
    incidents?: number;
    requests?: number;
    problems?: number;
    changes?: number;
  };
  tickets?: DeskTicket[];
  services?: DeskService[];
  articles?: DeskArticle[];
  slaAttainment?: number;
  mttrMs?: number | null;
  error?: string;
};

type SeverityMember = { id?: number; name?: string; email?: string; phone?: string };

type SeverityGroup = {
  id?: string;
  label?: string;
  priorities?: string[];
  sms?: boolean;
  meaning?: string;
  members?: SeverityMember[];
};

type SeverityDraft = { name: string; email: string; phone: string };

const DEFAULT_SEVERITY_GROUPS: SeverityGroup[] = [
  {
    id: 'critical',
    label: 'Critical',
    priorities: ['P1'],
    sms: true,
    meaning:
      'P1 — full outage or something that stops the business right now. Aim to respond in 15 minutes and restore within 4 hours. People here get an immediate email or SMS when a Critical ticket opens.',
    members: [],
  },
  {
    id: 'high',
    label: 'High',
    priorities: ['P2'],
    sms: true,
    meaning:
      'P2 — serious problem: major slowdown or many users blocked, but not a total outage. Aim to respond in 1 hour and restore within 8 hours. People here get an immediate email or SMS when a High ticket opens.',
    members: [],
  },
  {
    id: 'standard',
    label: 'Standard',
    priorities: ['P3'],
    sms: false,
    meaning:
      'P3 — normal day-to-day incidents and requests. Aim to respond in 4 hours and finish within 1 business day. These are not page-now alerts — they go on the Daily report below.',
    members: [],
  },
  {
    id: 'low',
    label: 'Low',
    priorities: ['P4'],
    sms: false,
    meaning:
      'P4 — minor issues, questions, or nice-to-haves that can wait. Aim to respond in 8 hours and finish within 3 days. These are not page-now alerts — they go on the Daily report below.',
    members: [],
  },
];

function realMemberEmail(email?: string): string {
  if (!email || /@complidesk\.local$/i.test(email)) return '';
  return email;
}

function memberLabel(person: SeverityMember): string {
  const email = realMemberEmail(person.email);
  return person.name || email || person.phone || 'Contact';
}

function emptySeverityDraft(): SeverityDraft {
  return { name: '', email: '', phone: '' };
}

type IntakeSource = {
  id?: string;
  enabled?: boolean;
  kind?: string;
  kindLabel?: string;
  kindSummary?: string;
  delivery?: string;
  address?: string;
  label?: string;
  detail?: string;
  source?: string;
  ci?: string;
};

type IntakeKindMeta = {
  id?: string;
  label?: string;
  delivery?: string;
  summary?: string;
};

type MailScanSettings = {
  mode?: 'unread' | 'newer' | 'unread_backlog' | 'all_recent';
  lookbackDays?: number;
  maxResults?: number;
  markRead?: 'none' | 'noise' | 'all';
};

type ImportantSummarySettings = {
  enabled?: boolean;
  emails?: string[];
  phones?: string[];
};

type SettingsPayload = {
  mailTriage?: {
    enabled?: boolean;
    mailbox?: string;
    mailboxes?: string[];
    lastRunAt?: string;
    intakeSources?: { sources?: IntakeSource[]; kinds?: IntakeKindMeta[] };
    scan?: MailScanSettings;
    importantSummary?: ImportantSummarySettings;
    lastSummary?: {
      scanned?: number;
      encodingRepaired?: number;
      markRead?: { marked?: number; error?: string };
    };
  };
  staff?: Array<{ email?: string; groups?: string[]; name?: string; phone?: string }>;
  severityGroups?: SeverityGroup[];
  dailyReport?: { enabled?: boolean; emails?: string[]; phones?: string[]; lastSentAt?: string | null };
  siteDiscovery?: { defaults?: string[]; last?: { scannedAt?: string; urls?: string[] } | null };
  lastAlert?: { delivery?: string; subject?: string; created_at?: string } | null;
  helpTopics?: Array<{ id?: string; label?: string; enabled?: boolean }>;
  intakeSources?: { sources?: IntakeSource[]; kinds?: IntakeKindMeta[] };
  error?: string;
};

type TriageRulesPayload = {
  rules?: Array<{ id?: string; name?: string; enabled?: boolean; action?: string }>;
  items?: Array<{ id?: string; name?: string; enabled?: boolean; action?: string }>;
  error?: string;
};

type CompliDeskWorkspaceProps = {
  language?: 'en' | 'es';
  gatewayBaseUrl?: string;
  accessToken?: string;
  onSignIn?: () => void;
  onAccessDenied?: () => void;
};

type DeskTab = 'queue' | 'services' | 'knowledge' | 'rules' | 'settings';

const QUEUE_LANES = [
  { id: 'new', labelEn: 'New', labelEs: 'Nuevos', hintEn: 'Just arrived', hintEs: 'Recién llegaron', statuses: ['new', 'draft'] },
  {
    id: 'triaged',
    labelEn: 'Triaged',
    labelEs: 'Clasificados',
    hintEn: 'Ready to pull',
    hintEs: 'Listos para tomar',
    statuses: ['triaged', 'assess', 'approved'],
  },
  {
    id: 'working',
    labelEn: 'Working',
    labelEs: 'En curso',
    hintEn: 'Actively in hand',
    hintEs: 'En manos del equipo',
    statuses: ['in_progress', 'implementing', 'known_error'],
  },
  {
    id: 'waiting',
    labelEn: 'Waiting',
    labelEs: 'En espera',
    hintEn: 'Blocked on someone',
    hintEs: 'Bloqueados por alguien',
    statuses: ['pending', 'review'],
  },
  {
    id: 'done',
    labelEn: 'Done',
    labelEs: 'Hechos',
    hintEn: 'Restored or closed',
    hintEs: 'Restaurados o cerrados',
    statuses: ['resolved', 'closed', 'fulfilled', 'rejected'],
  },
] as const;

function nextSteps(ticket: DeskTicket): Array<{ status: string; label: string; note: string; primary?: boolean }> {
  const steps: Array<{ status: string; label: string; note: string; primary?: boolean }> = [];
  const type = String(ticket.type || '');
  const status = String(ticket.status || '');
  if (type === 'incident') {
    if (status === 'new') steps.push({ status: 'triaged', label: 'Accept', note: 'Triaged.', primary: true });
    if (status === 'new' || status === 'triaged') {
      steps.push({ status: 'in_progress', label: 'Start work', note: 'Work started.', primary: status !== 'new' });
    }
    if (status === 'in_progress') {
      steps.push({ status: 'resolved', label: 'Mark restored', note: 'Service restored.', primary: true });
      steps.push({ status: 'pending', label: 'Waiting…', note: 'Waiting on someone.' });
    }
    if (status === 'pending') steps.push({ status: 'in_progress', label: 'Resume', note: 'Work resumed.', primary: true });
    if (status === 'resolved') steps.push({ status: 'closed', label: 'Close', note: 'Closed.', primary: true });
  } else if (type === 'request') {
    if (status === 'new') steps.push({ status: 'in_progress', label: 'Start', note: 'Fulfillment started.', primary: true });
    if (status === 'in_progress') {
      steps.push({ status: 'fulfilled', label: 'Mark fulfilled', note: 'Request fulfilled.', primary: true });
    }
    if (status === 'fulfilled') steps.push({ status: 'closed', label: 'Close', note: 'Closed.', primary: true });
  } else if (type === 'problem') {
    if (status === 'new') steps.push({ status: 'known_error', label: 'Known error', note: 'Root cause recorded.', primary: true });
    if (status === 'known_error') steps.push({ status: 'closed', label: 'Close problem', note: 'Problem closed.', primary: true });
  } else if (type === 'change') {
    if (status === 'draft') steps.push({ status: 'assess', label: 'Assess', note: 'Assessment started.', primary: true });
    if (status === 'assess') steps.push({ status: 'approved', label: 'Approve', note: 'Change approved.', primary: true });
    if (status === 'approved') steps.push({ status: 'implementing', label: 'Implement', note: 'Implementation started.', primary: true });
    if (status === 'implementing') steps.push({ status: 'review', label: 'Review', note: 'In review.', primary: true });
    if (status === 'review') steps.push({ status: 'closed', label: 'Close change', note: 'Change closed.', primary: true });
  }
  return steps;
}

function laneOf(ticket: DeskTicket): string {
  const status = String(ticket.status || '').toLowerCase();
  for (const lane of QUEUE_LANES) {
    if ((lane.statuses as readonly string[]).includes(status)) return lane.id;
  }
  return 'new';
}

function formatMttr(ms: number | null | undefined, spanish: boolean): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—';
  const hours = ms / 3_600_000;
  if (hours < 1) return spanish ? `${Math.round(ms / 60_000)} min` : `${Math.round(ms / 60_000)} min`;
  if (hours < 48) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} d`;
}

const INTAKE_KIND_ORDER = [
  'mailbox',
  'web_form',
  'website_chat',
  'help_menu',
  'research_case',
  'monitoring_probe',
  'application_event',
] as const;

function intakeKindHelp(kind: string, spanish: boolean): string {
  switch (kind) {
    case 'mailbox':
      return spanish
        ? 'Buzón de correo que la revisión diaria lee (pull).'
        : 'Mailbox the daily review scans (pull).';
    case 'web_form':
      return spanish
        ? 'Formulario público que envía una solicitud al escritorio (push).'
        : 'Public form that posts a request to the desk (push).';
    case 'website_chat':
      return spanish
        ? 'Chat del sitio que la IA puede enrutar al escritorio (push).'
        : 'Site chatbot turns the AI can route to the desk (push).';
    case 'help_menu':
      return spanish
        ? 'Opción del menú de ayuda que abre una solicitud (push).'
        : 'Help-menu option that opens a desk request (push).';
    case 'research_case':
      return spanish
        ? 'Defectos de investigación de Vantage o del gateway (push).'
        : 'Vantage / gateway research defects filed to the desk (push).';
    case 'monitoring_probe':
      return spanish
        ? 'Chequeo de salud o uptime que abre o cierra un incidente (agent).'
        : 'Uptime / health check that opens or resolves an incident (agent).';
    case 'application_event':
      return spanish
        ? 'Evento de una app (liquidez, API, ops) vía la API de issues (push).'
        : 'App event (liquidity, API, ops) via the issues API (push).';
    default:
      return spanish ? 'Canal que puede crear trabajo en el escritorio.' : 'Channel that can create desk work.';
  }
}

function intakeKindLabel(kind: string, meta: IntakeKindMeta | undefined, spanish: boolean): string {
  if (meta?.label) return meta.label;
  switch (kind) {
    case 'mailbox':
      return spanish ? 'Buzón' : 'Mailbox';
    case 'web_form':
      return spanish ? 'Formulario web' : 'Web form';
    case 'website_chat':
      return spanish ? 'Chat del sitio' : 'Website chat';
    case 'help_menu':
      return spanish ? 'Menú de ayuda' : 'Help menu';
    case 'research_case':
      return spanish ? 'Caso de investigación' : 'Research case';
    case 'monitoring_probe':
      return spanish ? 'Sonda de monitoreo' : 'Monitoring probe';
    case 'application_event':
      return spanish ? 'Evento de aplicación' : 'Application event';
    default:
      return kind || (spanish ? 'Otro' : 'Other');
  }
}

function intakeDeliveryLabel(delivery: string | undefined, spanish: boolean): string {
  switch (delivery) {
    case 'pull':
      return spanish ? 'pull (el escritorio busca)' : 'pull (desk fetches)';
    case 'push':
      return spanish ? 'push (llega solo)' : 'push (arrives on its own)';
    case 'agent':
      return spanish ? 'agent (proceso del escritorio)' : 'agent (desk process)';
    default:
      return delivery || '';
  }
}

function groupIntakeSources(sources: IntakeSource[]): Array<{ kind: string; rows: IntakeSource[] }> {
  const byKind = new Map<string, IntakeSource[]>();
  for (const source of sources) {
    const kind = String(source.kind || 'other');
    const bucket = byKind.get(kind) || [];
    bucket.push(source);
    byKind.set(kind, bucket);
  }
  const ordered: Array<{ kind: string; rows: IntakeSource[] }> = [];
  for (const kind of INTAKE_KIND_ORDER) {
    const rows = byKind.get(kind);
    if (rows?.length) ordered.push({ kind, rows });
    byKind.delete(kind);
  }
  for (const [kind, rows] of byKind) {
    ordered.push({ kind, rows });
  }
  return ordered;
}

const CompliDeskWorkspace: React.FC<CompliDeskWorkspaceProps> = ({
  language = 'en',
  gatewayBaseUrl = '',
  accessToken = '',
  onSignIn,
  onAccessDenied,
}) => {
  const spanish = language === 'es';
  const apiBase = gatewayBaseUrl.replace(/\/$/, '');
  const [tab, setTab] = React.useState<DeskTab>('queue');
  const [board, setBoard] = React.useState<BoardPayload | null>(null);
  const [settings, setSettings] = React.useState<SettingsPayload | null>(null);
  const [rules, setRules] = React.useState<TriageRulesPayload | null>(null);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [triageBusy, setTriageBusy] = React.useState(false);
  const [triageNote, setTriageNote] = React.useState('');
  const [digestEmails, setDigestEmails] = React.useState('');
  const [digestPhones, setDigestPhones] = React.useState('');
  const [digestNote, setDigestNote] = React.useState('');
  const [digestBusy, setDigestBusy] = React.useState(false);
  const [scanUrls, setScanUrls] = React.useState('');
  const [scanNote, setScanNote] = React.useState('');
  const [scanBusy, setScanBusy] = React.useState(false);
  const [selectedSourceIds, setSelectedSourceIds] = React.useState<Set<string>>(new Set());
  const [mailTriageEnabled, setMailTriageEnabled] = React.useState(false);
  const [scanMode, setScanMode] = React.useState<MailScanSettings['mode']>('unread');
  const [scanLookbackDays, setScanLookbackDays] = React.useState(14);
  const [scanMarkRead, setScanMarkRead] = React.useState<MailScanSettings['markRead']>('noise');
  const [importantEmails, setImportantEmails] = React.useState('');
  const [importantPhones, setImportantPhones] = React.useState('');
  const [customMailbox, setCustomMailbox] = React.useState('');
  const [customMailboxLabel, setCustomMailboxLabel] = React.useState('');
  const [customMailboxGmailLabel, setCustomMailboxGmailLabel] = React.useState('');
  const [intakeBusy, setIntakeBusy] = React.useState(false);
  const [intakeNote, setIntakeNote] = React.useState('');
  const [openSeverityIds, setOpenSeverityIds] = React.useState<Set<string>>(() => new Set());
  const [addFormGroupId, setAddFormGroupId] = React.useState<string | null>(null);
  const [severityDrafts, setSeverityDrafts] = React.useState<Record<string, SeverityDraft>>({});
  const [severityBusy, setSeverityBusy] = React.useState(false);
  const [severityNote, setSeverityNote] = React.useState('');
  const [selectedTicketId, setSelectedTicketId] = React.useState<number | null>(null);
  const [ticketDetail, setTicketDetail] = React.useState<DeskTicket | null>(null);
  const [ticketBusy, setTicketBusy] = React.useState(false);
  const [ticketError, setTicketError] = React.useState('');

  const deskFetch = React.useCallback(
    async (path: string, init?: RequestInit) => {
      const response = await fetch(`${apiBase}/api/v1/complidesk/desk/${path}`, {
        ...init,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...(init?.headers || {}),
        },
      });
      const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
      return { response, body };
    },
    [accessToken, apiBase],
  );

  const handleAuth = React.useCallback(
    (status: number) => {
      if (status === 401) {
        setError(spanish ? 'La sesión expiró. Inicie sesión de nuevo.' : 'Session expired. Sign in again.');
        onSignIn?.();
        return true;
      }
      if (status === 403) {
        setError(
          spanish ? 'Este plan no incluye CompliDesk.' : 'This plan does not include CompliDesk.',
        );
        onAccessDenied?.();
        return true;
      }
      return false;
    },
    [onAccessDenied, onSignIn, spanish],
  );

  const openTicket = React.useCallback(async (id: number) => {
    setSelectedTicketId(id);
    setTicketBusy(true);
    setTicketError('');
    try {
      const { response, body } = await deskFetch(`tickets/${id}`);
      if (handleAuth(response.status)) return;
      if (!response.ok) {
        setTicketError(String(body.detail || (spanish ? 'No se pudo abrir el ticket.' : 'Could not open the ticket.')));
        setTicketDetail(null);
        return;
      }
      setTicketDetail(body as DeskTicket);
    } catch {
      setTicketError(spanish ? 'No se pudo abrir el ticket.' : 'Could not open the ticket.');
      setTicketDetail(null);
    } finally {
      setTicketBusy(false);
    }
  }, [deskFetch, handleAuth, spanish]);

  const closeTicketDrawer = React.useCallback(() => {
    setSelectedTicketId(null);
    setTicketDetail(null);
    setTicketError('');
  }, []);

  const loadBoard = React.useCallback(async () => {
    if (!apiBase || !accessToken) {
      setError(spanish ? 'Inicie sesión para abrir CompliDesk.' : 'Sign in to open CompliDesk.');
      setBoard(null);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { response, body } = await deskFetch('board');
      if (handleAuth(response.status)) {
        setBoard(null);
        return;
      }
      if (!response.ok) {
        setBoard(null);
        setError(
          String(
            body.detail || body.error || (spanish ? 'No se pudo cargar el escritorio.' : 'Could not load the desk.'),
          ),
        );
        return;
      }
      setBoard(body as BoardPayload);
    } catch {
      setBoard(null);
      setError(spanish ? 'No se pudo contactar CompliDesk.' : 'Could not reach CompliDesk.');
    } finally {
      setBusy(false);
    }
  }, [accessToken, apiBase, deskFetch, handleAuth, spanish]);

  const applyTransition = React.useCallback(async (id: number, status: string, note: string) => {
    setTicketBusy(true);
    setTicketError('');
    try {
      const { response, body } = await deskFetch(`tickets/${id}/transition`, {
        method: 'POST',
        body: JSON.stringify({ status, note: note || '' }),
      });
      if (handleAuth(response.status)) return;
      if (!response.ok) {
        setTicketError(String(body.detail || (spanish ? 'No se pudo actualizar el ticket.' : 'Could not update the ticket.')));
        return;
      }
      await loadBoard();
      await openTicket(id);
    } catch {
      setTicketError(spanish ? 'No se pudo actualizar el ticket.' : 'Could not update the ticket.');
    } finally {
      setTicketBusy(false);
    }
  }, [deskFetch, handleAuth, loadBoard, openTicket, spanish]);

  const loadSettings = React.useCallback(async () => {
    if (!apiBase || !accessToken) return;
    try {
      const { response, body } = await deskFetch('settings');
      if (handleAuth(response.status)) return;
      if (!response.ok) {
        setSettings({
          error: String(body.detail || body.error || (spanish ? 'No se pudieron cargar los ajustes.' : 'Could not load settings.')),
        });
        return;
      }
      const next = body as SettingsPayload;
      setSettings(next);
      setDigestEmails((next.dailyReport?.emails || []).join('\n'));
      setDigestPhones((next.dailyReport?.phones || []).join('\n'));
      const defaults = next.siteDiscovery?.defaults || [];
      const lastUrls = next.siteDiscovery?.last?.urls || [];
      setScanUrls((lastUrls.length ? lastUrls : defaults).join('\n'));
      const sources = next.mailTriage?.intakeSources?.sources || next.intakeSources?.sources || [];
      setSelectedSourceIds(
        new Set(sources.filter((row) => row.enabled !== false && row.id).map((row) => String(row.id))),
      );
      setMailTriageEnabled(Boolean(next.mailTriage?.enabled));
      setScanMode(next.mailTriage?.scan?.mode || 'unread');
      setScanLookbackDays(Number(next.mailTriage?.scan?.lookbackDays) || 14);
      setScanMarkRead(next.mailTriage?.scan?.markRead || 'noise');
      setImportantEmails((next.mailTriage?.importantSummary?.emails || []).join('\n'));
      setImportantPhones((next.mailTriage?.importantSummary?.phones || []).join('\n'));
      setIntakeNote('');
    } catch {
      setSettings({ error: spanish ? 'No se pudieron cargar los ajustes.' : 'Could not load settings.' });
    }
  }, [accessToken, apiBase, deskFetch, handleAuth, spanish]);

  const loadRules = React.useCallback(async () => {
    if (!apiBase || !accessToken) return;
    try {
      const { response, body } = await deskFetch('triage-rules');
      if (handleAuth(response.status)) return;
      if (!response.ok) {
        setRules({
          error: String(body.detail || body.error || (spanish ? 'No se pudieron cargar las reglas.' : 'Could not load rules.')),
        });
        return;
      }
      setRules(body as TriageRulesPayload);
    } catch {
      setRules({ error: spanish ? 'No se pudieron cargar las reglas.' : 'Could not load rules.' });
    }
  }, [accessToken, apiBase, deskFetch, handleAuth, spanish]);

  React.useEffect(() => {
    void loadBoard();
  }, [loadBoard]);

  React.useEffect(() => {
    if (tab === 'settings') void loadSettings();
    if (tab === 'rules') void loadRules();
  }, [tab, loadSettings, loadRules]);

  const saveDailyReport = React.useCallback(async () => {
    setDigestBusy(true);
    setDigestNote('');
    try {
      const hasContacts = [digestEmails, digestPhones].some((value) => value.trim().length > 0);
      const { response, body } = await deskFetch('settings', {
        method: 'PUT',
        body: JSON.stringify({
          dailyReport: { enabled: hasContacts, emails: digestEmails, phones: digestPhones },
        }),
      });
      if (handleAuth(response.status)) return;
      if (!response.ok) {
        setDigestNote(String(body.detail || body.error || (spanish ? 'No se pudo guardar.' : 'Could not save.')));
        return;
      }
      setDigestNote(
        hasContacts
          ? spanish
            ? 'Informe diario guardado.'
            : 'Saved daily report signup.'
          : spanish
            ? 'Contactos borrados — el informe no se enviará.'
            : 'Cleared daily report contacts — digest will not send.',
      );
      await loadSettings();
    } catch {
      setDigestNote(spanish ? 'No se pudo guardar el informe diario.' : 'Could not save daily report.');
    } finally {
      setDigestBusy(false);
    }
  }, [deskFetch, digestEmails, digestPhones, handleAuth, loadSettings, spanish]);

  const runSiteDiscovery = React.useCallback(async () => {
    setScanBusy(true);
    setScanNote(spanish ? 'Explorando páginas permitidas…' : 'Scanning allowlisted pages…');
    try {
      const urls = scanUrls
        .split(/\n+/)
        .map((item) => item.trim())
        .filter(Boolean);
      const { response, body } = await deskFetch('site-discovery/scan', {
        method: 'POST',
        body: JSON.stringify({ urls }),
      });
      if (handleAuth(response.status)) return;
      if (!response.ok) {
        setScanNote(String(body.detail || body.error || (spanish ? 'Falló el escaneo.' : 'Scan failed.')));
        return;
      }
      const scannedAt = String((body as { findings?: { scannedAt?: string } }).findings?.scannedAt || '');
      setScanNote(
        spanish
          ? `Escaneo terminado${scannedAt ? ` a las ${scannedAt}` : ''}.`
          : `Scan finished${scannedAt ? ` at ${scannedAt}` : ''}.`,
      );
      if ((body as { settings?: SettingsPayload }).settings) {
        setSettings((body as { settings: SettingsPayload }).settings);
      } else {
        await loadSettings();
      }
    } catch {
      setScanNote(spanish ? 'No se pudo escanear el sitio.' : 'Could not scan the website.');
    } finally {
      setScanBusy(false);
    }
  }, [deskFetch, handleAuth, loadSettings, scanUrls, spanish]);

  const runMailTriage = React.useCallback(async () => {
    setTriageBusy(true);
    setTriageNote('');
    try {
      const { response, body } = await deskFetch('mail-triage/run', { method: 'POST', body: '{}' });
      if (handleAuth(response.status)) return;
      if (!response.ok) {
        setTriageNote(String(body.detail || body.error || (spanish ? 'Falló la triage de correo.' : 'Mail triage failed.')));
        return;
      }
      const results = Array.isArray((body as { results?: Array<{ created?: boolean }> }).results)
        ? (body as { results: Array<{ created?: boolean }> }).results
        : [];
      const created = results.filter((row) => row.created).length;
      setTriageNote(
        spanish
          ? `Triage de correo terminó. Tickets nuevos: ${created}.`
          : `Mail triage finished. New tickets: ${created}.`,
      );
      await loadBoard();
      await loadSettings();
    } catch {
      setTriageNote(spanish ? 'No se pudo ejecutar la triage de correo.' : 'Could not run mail triage.');
    } finally {
      setTriageBusy(false);
    }
  }, [deskFetch, handleAuth, loadBoard, loadSettings, spanish]);

  const toggleIntakeSource = React.useCallback((sourceId: string, checked: boolean) => {
    setSelectedSourceIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(sourceId);
      else next.delete(sourceId);
      return next;
    });
  }, []);

  const saveIntakeSources = React.useCallback(async () => {
    setIntakeBusy(true);
    setIntakeNote('');
    try {
      const addMailbox = customMailbox.trim()
        ? {
            address: customMailbox.trim(),
            label: customMailboxLabel.trim() || customMailbox.trim(),
            gmailLabel: customMailboxGmailLabel.trim(),
          }
        : null;
      const { response, body } = await deskFetch('settings', {
        method: 'PUT',
        body: JSON.stringify({
          mailTriage: {
            sourceIds: [...selectedSourceIds],
            enabled: mailTriageEnabled,
            scan: {
              mode: scanMode,
              lookbackDays: scanLookbackDays,
              markRead: scanMarkRead,
            },
            importantSummary: {
              enabled: Boolean(importantEmails.trim() || importantPhones.trim()),
              emails: importantEmails,
              phones: importantPhones,
            },
            ...(addMailbox ? { addMailbox } : {}),
          },
        }),
      });
      if (handleAuth(response.status)) return;
      if (!response.ok) {
        setIntakeNote(String(body.detail || body.error || (spanish ? 'No se pudo guardar.' : 'Could not save.')));
        return;
      }
      setCustomMailbox('');
      setCustomMailboxLabel('');
      setCustomMailboxGmailLabel('');
      setIntakeNote(spanish ? 'Fuentes de intake y ajustes de correo guardados.' : 'Saved intake sources and mail settings.');
      await loadSettings();
    } catch {
      setIntakeNote(spanish ? 'No se pudieron guardar las fuentes.' : 'Could not save intake sources.');
    } finally {
      setIntakeBusy(false);
    }
  }, [
    customMailbox,
    customMailboxGmailLabel,
    customMailboxLabel,
    deskFetch,
    handleAuth,
    importantEmails,
    importantPhones,
    loadSettings,
    mailTriageEnabled,
    scanLookbackDays,
    scanMarkRead,
    scanMode,
    selectedSourceIds,
    spanish,
  ]);

  const toggleSeverityGroup = React.useCallback((groupId: string) => {
    setOpenSeverityIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const applySeverityGroups = React.useCallback((groups: SeverityGroup[] | undefined) => {
    if (!groups) return;
    setSettings((prev) => (prev ? { ...prev, severityGroups: groups, error: undefined } : { severityGroups: groups }));
  }, []);

  const addSeverityMember = React.useCallback(
    async (groupId: string) => {
      const draft = severityDrafts[groupId] || emptySeverityDraft();
      const email = draft.email.trim();
      const phone = draft.phone.trim();
      if (!email && !phone) {
        setSeverityNote(
          spanish ? 'Agregue un correo o un teléfono.' : 'Add an email address or a phone number.',
        );
        return;
      }
      setSeverityBusy(true);
      setSeverityNote('');
      try {
        const { response, body } = await deskFetch(`severity-groups/${groupId}/members`, {
          method: 'POST',
          body: JSON.stringify({
            name: draft.name.trim(),
            email,
            phone,
          }),
        });
        if (handleAuth(response.status)) return;
        if (!response.ok) {
          setSeverityNote(
            String(body.detail || body.error || (spanish ? 'No se pudo agregar.' : 'Could not add contact.')),
          );
          return;
        }
        const groups = (body.severityGroups as SeverityGroup[] | undefined) || undefined;
        if (groups) applySeverityGroups(groups);
        else await loadSettings();
        setSeverityDrafts((prev) => ({ ...prev, [groupId]: emptySeverityDraft() }));
        setAddFormGroupId(null);
        setOpenSeverityIds((prev) => new Set(prev).add(groupId));
        const label =
          (groups || settings?.severityGroups || []).find((row) => row.id === groupId)?.label || groupId;
        setSeverityNote(spanish ? `Agregado a ${label}.` : `Added to ${label}.`);
      } catch {
        setSeverityNote(spanish ? 'No se pudo agregar el contacto.' : 'Could not add contact.');
      } finally {
        setSeverityBusy(false);
      }
    },
    [applySeverityGroups, deskFetch, handleAuth, loadSettings, settings?.severityGroups, severityDrafts, spanish],
  );

  const removeSeverityMember = React.useCallback(
    async (groupId: string, staffId: number, label: string) => {
      setSeverityBusy(true);
      setSeverityNote('');
      try {
        const { response, body } = await deskFetch(`severity-groups/${groupId}/members/${staffId}`, {
          method: 'DELETE',
        });
        if (handleAuth(response.status)) return;
        if (!response.ok) {
          setSeverityNote(
            String(body.detail || body.error || (spanish ? 'No se pudo quitar.' : 'Could not remove contact.')),
          );
          return;
        }
        const groups = (body.severityGroups as SeverityGroup[] | undefined) || undefined;
        if (groups) applySeverityGroups(groups);
        else await loadSettings();
        setSeverityNote(
          body.emptied
            ? spanish
              ? `Quitado de ${label} (y del personal — sin otros grupos).`
              : `Removed from ${label} (and from staff — no other groups left).`
            : spanish
              ? `Quitado de ${label}.`
              : `Removed from ${label}.`,
        );
      } catch {
        setSeverityNote(spanish ? 'No se pudo quitar el contacto.' : 'Could not remove contact.');
      } finally {
        setSeverityBusy(false);
      }
    },
    [applySeverityGroups, deskFetch, handleAuth, loadSettings, spanish],
  );

  const tickets = board?.tickets || [];
  const byLane = Object.fromEntries(QUEUE_LANES.map((lane) => [lane.id, [] as DeskTicket[]]));
  for (const ticket of tickets) {
    byLane[laneOf(ticket)].push(ticket);
  }

  const tabs: Array<{ id: DeskTab; label: string }> = [
    { id: 'queue', label: spanish ? 'Cola' : 'Queue' },
    { id: 'services', label: spanish ? 'Servicios' : 'Services' },
    { id: 'knowledge', label: spanish ? 'Conocimiento' : 'Knowledge' },
    { id: 'rules', label: spanish ? 'Reglas de triage' : 'Triage Rules' },
    { id: 'settings', label: spanish ? 'Ajustes' : 'Settings' },
  ];

  const itilHelp = spanish
    ? 'ITIL es un libro de prácticas para operar servicios de TI: incidentes (restaurar el servicio), solicitudes (cumplir un pedido), problemas (quitar la causa que se repite) y cambios (poner algo en marcha con control). CompliDesk usa esas prácticas en un tablero compartido.'
    : 'ITIL is a playbook of practices for running IT services: incidents (restore the service), requests (fulfill something asked for), problems (remove a repeating cause), and changes (put something live with control). CompliDesk uses those practices on a shared board.';

  const intakeCatalog = settings?.mailTriage?.intakeSources || settings?.intakeSources;
  const intakeSources = intakeCatalog?.sources || [];
  const intakeKinds = intakeCatalog?.kinds || [];
  const intakeGroups = groupIntakeSources(intakeSources);
  const severityGroups = (settings?.severityGroups?.length
    ? settings.severityGroups
    : DEFAULT_SEVERITY_GROUPS
  ).map((group) => {
    const fallback = DEFAULT_SEVERITY_GROUPS.find((row) => row.id === group.id);
    return {
      ...fallback,
      ...group,
      meaning: group.meaning || fallback?.meaning,
      members: group.members || [],
    };
  });
  const ruleRows = rules?.rules || rules?.items || [];

  return (
    <section className="complidesk-workspace complidesk-workspace--host" aria-label="CompliDesk">
      <header className="complidesk-host-head">
        <div className="complidesk-host-copy">
          <h2>
            CompliDesk
            <HelpBadge label={spanish ? 'Qué es CompliDesk' : 'What CompliDesk is'}>
              {spanish
                ? 'App de suscripción en esta consola (menú izquierdo y superior). El gateway habla con desk.nextcompli.com por API — no abre una pestaña aparte ni un iframe.'
                : 'Subscription app inside this console (left and top menus). The gateway talks to desk.nextcompli.com over API — no separate tab and no iframe.'}
            </HelpBadge>
          </h2>
          <p className="complidesk-host-lede">
            {spanish ? (
              <>
                Escritorio{' '}
                <GlossaryTerm term="ITIL" definition={itilHelp}>
                  ITIL
                </GlossaryTerm>{' '}
                del cliente NextCompli, dentro del shell de la consola.
              </>
            ) : (
              <>
                NextCompli client{' '}
                <GlossaryTerm term="ITIL" definition={itilHelp}>
                  ITIL
                </GlossaryTerm>{' '}
                desk inside the console shell.
              </>
            )}
          </p>
        </div>
        <button type="button" className="dashboard-btn" disabled={busy} onClick={() => void loadBoard()}>
          {busy ? (spanish ? 'Cargando…' : 'Loading…') : spanish ? 'Actualizar' : 'Refresh'}
        </button>
      </header>

      <nav className="complidesk-console-tabs" aria-label={spanish ? 'Secciones de CompliDesk' : 'CompliDesk sections'}>
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={tab === item.id ? 'is-active' : undefined}
            onClick={() => {
              setTab(item.id);
              if (item.id !== 'queue') closeTicketDrawer();
            }}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {error ? (
        <p className="complidesk-host-error" role="alert">
          {error}
        </p>
      ) : null}

      {tab === 'queue' && board ? (
        <>
          <ul className="complidesk-bff-kpis" aria-label={spanish ? 'Resumen del escritorio' : 'Desk summary'}>
            <li>
              <strong>{board.counts?.open ?? 0}</strong>
              <span>{spanish ? 'abiertos' : 'open'}</span>
            </li>
            <li>
              <strong>{board.counts?.breached ?? 0}</strong>
              <span>{spanish ? 'pasados de reloj' : 'past the clock'}</span>
            </li>
            <li>
              <strong>{board.slaAttainment ?? '—'}%</strong>
              <span>{spanish ? 'SLA cumplido' : 'SLA met'}</span>
            </li>
            <li>
              <strong>{formatMttr(board.mttrMs, spanish)}</strong>
              <span>{spanish ? 'tiempo medio a restaurar' : 'mean time to restore'}</span>
            </li>
          </ul>
          {(board.counts?.open ?? 0) === 0 ? (
            <p className="complidesk-host-notice" role="status">
              {spanish
                ? 'Los incidentes y solicitudes aparecen aquí en los carriles de estado (Nuevo → Hecho). Aún no hay tickets: active fuentes de intake en Ajustes, o espere un push del sitio. Un tablero vacío es normal — no es una sesión rota.'
                : 'Incidents and requests show here in the status swim lanes (New → Done). No tickets yet — enable intake sources under Settings, or wait for a push from the website. An empty board is normal — not a broken session.'}
            </p>
          ) : null}
          <p className="complidesk-bff-hint complidesk-queue-lanes-hint">
            {spanish
              ? `Carriles por estado del ticket. Incidentes abiertos: ${board.counts?.incidents ?? 0} · Solicitudes: ${board.counts?.requests ?? 0}.`
              : `Swim lanes are by ticket status. Open incidents: ${board.counts?.incidents ?? 0} · Requests: ${board.counts?.requests ?? 0}.`}
          </p>
          <div className={`complidesk-queue-layout${selectedTicketId ? ' has-drawer' : ''}`}>
          <div className="complidesk-bff-board complidesk-bff-board--status" role="list">
            {QUEUE_LANES.map((lane) => {
              const rows = byLane[lane.id] || [];
              return (
                <section key={lane.id} className="complidesk-bff-lane" aria-label={spanish ? lane.labelEs : lane.labelEn}>
                  <header>
                    <div>
                      <h3>{spanish ? lane.labelEs : lane.labelEn}</h3>
                      <p className="complidesk-bff-hint">{spanish ? lane.hintEs : lane.hintEn}</p>
                    </div>
                    <span>{rows.length}</span>
                  </header>
                  <ul>
                    {rows.length === 0 ? (
                      <li className="complidesk-bff-empty">{spanish ? 'Nada aquí' : 'Nothing here'}</li>
                    ) : (
                      rows.slice(0, 40).map((ticket) => {
                        const ticketId = Number(ticket.id);
                        const selected = selectedTicketId === ticketId;
                        const label = `${ticket.number || `#${ticket.id}`} ${ticket.title || (spanish ? 'Sin título' : 'Untitled')}`;
                        return (
                          <li key={String(ticket.id || ticket.number)} className={selected ? 'is-selected' : undefined}>
                            <button
                              type="button"
                              className={`complidesk-ticket-card${selected ? ' is-selected' : ''}`}
                              aria-pressed={selected}
                              aria-label={spanish ? `Abrir ${label}` : `Open ${label}`}
                              onClick={() => {
                                if (!Number.isFinite(ticketId)) return;
                                void openTicket(ticketId);
                              }}
                            >
                              <strong>{ticket.number || `#${ticket.id}`}</strong>
                              <span className="complidesk-bff-title">
                                {ticket.title || (spanish ? 'Sin título' : 'Untitled')}
                              </span>
                              <span className="complidesk-bff-meta">
                                {[ticket.type || (spanish ? 'ticket' : 'ticket'), ticket.priority, ticket.status]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </span>
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                </section>
              );
            })}
          </div>
          {selectedTicketId ? (
            <aside className="complidesk-ticket-drawer" aria-label={spanish ? 'Detalle del ticket' : 'Ticket detail'}>
              <header className="complidesk-ticket-drawer-head">
                <h3>
                  {ticketDetail
                    ? `${ticketDetail.number || `#${ticketDetail.id}`} ${ticketDetail.title || ''}`.trim()
                    : (spanish ? 'Ticket' : 'Ticket')}
                </h3>
                <button type="button" className="dashboard-btn" onClick={closeTicketDrawer}>
                  {spanish ? 'Cerrar' : 'Close'}
                </button>
              </header>
              {ticketBusy && !ticketDetail ? (
                <p className="complidesk-bff-hint">{spanish ? 'Cargando…' : 'Loading…'}</p>
              ) : null}
              {ticketError ? (
                <p className="complidesk-host-error" role="alert">{ticketError}</p>
              ) : null}
              {ticketDetail ? (
                <>
                  <p className="complidesk-ticket-pills" aria-label={spanish ? 'Estado' : 'Status'}>
                    {[ticketDetail.type, ticketDetail.priority, ticketDetail.status, ticketDetail.sla?.state?.replaceAll('_', ' ')]
                      .filter(Boolean)
                      .map((label) => (
                        <span key={String(label)} className="complidesk-ticket-pill">{label}</span>
                      ))}
                  </p>
                  {ticketDetail.detail ? (
                    <section className="complidesk-ticket-section" aria-label={spanish ? 'Detalle' : 'Detail'}>
                      <h4>{spanish ? 'Detalle' : 'Detail'}</h4>
                      <pre className="complidesk-ticket-detail">{ticketDetail.detail}</pre>
                    </section>
                  ) : null}
                  <div className="complidesk-ticket-actions">
                    {nextSteps(ticketDetail).map((step) => (
                      <button
                        key={`${step.status}-${step.label}`}
                        type="button"
                        className={`dashboard-btn${step.primary ? ' dashboard-btn-primary' : ''}`}
                        disabled={ticketBusy || !ticketDetail.id}
                        onClick={() => {
                          if (!ticketDetail.id) return;
                          void applyTransition(Number(ticketDetail.id), step.status, step.note);
                        }}
                      >
                        {step.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="dashboard-btn"
                      onClick={() => {
                        closeTicketDrawer();
                        setTab('rules');
                      }}
                    >
                      {spanish ? 'Editar reglas de triage' : 'Edit triage rules'}
                    </button>
                  </div>
                  {(ticketDetail.notes || []).length ? (
                    <section className="complidesk-ticket-section" aria-label={spanish ? 'Notas' : 'Notes'}>
                      <h4>{spanish ? 'Notas' : 'Notes'}</h4>
                      <ul className="complidesk-ticket-notes">
                        {(ticketDetail.notes || []).map((note, index) => (
                          <li key={`${note.at || index}-${(note.body || '').slice(0, 24)}`}>
                            {note.body || '—'}
                          </li>
                        ))}
                      </ul>
                    </section>
                  ) : null}
                </>
              ) : null}
            </aside>
          ) : null}
          </div>
        </>
      ) : null}

      {tab === 'services' && board ? (
        <ul className="complidesk-bff-list" aria-label={spanish ? 'Servicios' : 'Services'}>
          {(board.services || []).length === 0 ? (
            <li className="complidesk-bff-empty">{spanish ? 'Sin servicios aún.' : 'No services yet.'}</li>
          ) : (
            (board.services || []).map((service) => (
              <li key={String(service.id || service.name)}>
                <strong>{service.name}</strong>
                <span className="complidesk-bff-meta">{service.description || '—'}</span>
              </li>
            ))
          )}
        </ul>
      ) : null}

      {tab === 'knowledge' && board ? (
        <ul className="complidesk-bff-list" aria-label={spanish ? 'Conocimiento' : 'Knowledge'}>
          {(board.articles || []).length === 0 ? (
            <li className="complidesk-bff-empty">{spanish ? 'Sin artículos aún.' : 'No articles yet.'}</li>
          ) : (
            (board.articles || []).map((article) => (
              <li key={String(article.id || article.title)}>
                <strong>{article.title}</strong>
                <span className="complidesk-bff-meta">{(article.body || '').slice(0, 160) || '—'}</span>
              </li>
            ))
          )}
        </ul>
      ) : null}

      {tab === 'rules' ? (
        rules?.error ? (
          <p className="complidesk-host-error" role="alert">
            {rules.error}
          </p>
        ) : (
          <ul className="complidesk-bff-list" aria-label={spanish ? 'Reglas de triage' : 'Triage rules'}>
            {ruleRows.length === 0 ? (
              <li className="complidesk-bff-empty">{spanish ? 'Sin reglas aún.' : 'No rules yet.'}</li>
            ) : (
              ruleRows.map((rule, index) => (
                <li key={String(rule.id || index)}>
                  <strong>{rule.name || rule.id || `Rule ${index + 1}`}</strong>
                  <span className="complidesk-bff-meta">
                    {[rule.enabled === false ? (spanish ? 'apagada' : 'off') : spanish ? 'activa' : 'on', rule.action]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </li>
              ))
            )}
          </ul>
        )
      ) : null}

      {tab === 'settings' ? (
        settings?.error ? (
          <p className="complidesk-host-error" role="alert">
            {settings.error}
          </p>
        ) : (
          <div className="complidesk-settings-panel complidesk-settings-panel--full">
            <div className="complidesk-settings-top-row">
            <section className="complidesk-settings-block" aria-labelledby="complidesk-alerts-heading">
              <h3 id="complidesk-alerts-heading">{spanish ? 'Quién recibe alertas' : 'Who gets alerted'}</h3>
              <p>
                {spanish
                  ? 'Abra Critical o High para paginar de inmediato. Standard y Low van al informe diario de abajo.'
                  : 'Open Critical or High for immediate pages. Standard and Low go on the Daily report below.'}
              </p>
              <p className="complidesk-bff-meta" role="status">
                {settings?.lastAlert
                  ? spanish
                    ? `Última alerta enviada: ${settings.lastAlert.subject || '—'}${
                        settings.lastAlert.delivery ? ` — ${settings.lastAlert.delivery}` : ''
                      }`
                    : `Last alert sent: ${settings.lastAlert.subject || '—'}${
                        settings.lastAlert.delivery ? ` — ${settings.lastAlert.delivery}` : ''
                      }`
                  : spanish
                    ? 'Aún no se ha enviado una alerta.'
                    : 'No alert has been sent yet.'}
              </p>
              <div
                className="complidesk-severity-accordion"
                aria-label={spanish ? 'Grupos de severidad' : 'Severity groups'}
              >
                {severityGroups.map((group) => {
                  const groupId = String(group.id || group.label || 'group');
                  const open = openSeverityIds.has(groupId);
                  const count = (group.members || []).length;
                  const channel = group.sms
                    ? spanish
                      ? 'correo o SMS'
                      : 'email or SMS'
                    : spanish
                      ? 'correo'
                      : 'email';
                  const draft = severityDrafts[groupId] || emptySeverityDraft();
                  const formOpen = addFormGroupId === groupId;
                  const priorityLabel = (group.priorities || []).join(', ') || '—';
                  return (
                    <section key={groupId} className="complidesk-severity-acc" data-group-id={groupId}>
                      <h4 className="complidesk-severity-acc-heading">
                        <button
                          type="button"
                          className="complidesk-severity-acc-trigger"
                          id={`sev-btn-${groupId}`}
                          aria-expanded={open}
                          aria-controls={`sev-panel-${groupId}`}
                          onClick={() => toggleSeverityGroup(groupId)}
                        >
                          <span className="complidesk-severity-acc-caret" aria-hidden="true" />
                          <span className="complidesk-severity-acc-title">{group.label || groupId}</span>
                          <span className="complidesk-sev-pill">{priorityLabel}</span>
                          <span className="complidesk-sev-pill">
                            {count === 1
                              ? spanish
                                ? '1 persona'
                                : '1 person'
                              : spanish
                                ? `${count} personas`
                                : `${count} people`}
                          </span>
                          <span className="complidesk-bff-meta">{channel}</span>
                        </button>
                      </h4>
                      <div
                        className="complidesk-severity-acc-panel"
                        id={`sev-panel-${groupId}`}
                        role="region"
                        aria-labelledby={`sev-btn-${groupId}`}
                        hidden={!open}
                      >
                        {group.meaning ? (
                          <p className="complidesk-severity-meaning">{group.meaning}</p>
                        ) : null}
                        <ul className="complidesk-severity-members">
                          {(group.members || []).length === 0 ? (
                            <li className="complidesk-bff-meta complidesk-severity-empty">
                              {spanish
                                ? 'Nadie en este grupo aún. Use + para agregar un contacto.'
                                : 'No one in this group yet. Use + to add a contact.'}
                            </li>
                          ) : (
                            (group.members || []).map((person) => {
                              const label = memberLabel(person);
                              const email = realMemberEmail(person.email);
                              const metaParts = [
                                email && email !== label ? email : '',
                                person.phone && person.phone !== label ? person.phone : '',
                              ].filter(Boolean);
                              return (
                                <li key={String(person.id || label)} className="complidesk-severity-member">
                                  <div>
                                    <strong>{label}</strong>
                                    <div className="complidesk-bff-meta">
                                      {metaParts.join(' · ') || (group.sms ? 'SMS' : spanish ? 'correo' : 'email')}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="complidesk-icon-btn complidesk-icon-btn--danger"
                                    disabled={severityBusy || person.id == null}
                                    aria-label={
                                      spanish
                                        ? `Quitar ${label} de ${group.label || groupId}`
                                        : `Remove ${label} from ${group.label || groupId}`
                                    }
                                    onClick={() => {
                                      if (person.id == null) return;
                                      void removeSeverityMember(groupId, Number(person.id), group.label || groupId);
                                    }}
                                  >
                                    −
                                  </button>
                                </li>
                              );
                            })
                          )}
                        </ul>
                        <div className="complidesk-severity-member-toolbar">
                          <button
                            type="button"
                            className="complidesk-icon-btn complidesk-icon-btn--add"
                            aria-label={
                              spanish
                                ? `Agregar persona a ${group.label || groupId}`
                                : `Add person to ${group.label || groupId}`
                            }
                            aria-expanded={formOpen}
                            disabled={severityBusy}
                            onClick={() => {
                              setAddFormGroupId((prev) => (prev === groupId ? null : groupId));
                              setSeverityDrafts((prev) =>
                                prev[groupId] ? prev : { ...prev, [groupId]: emptySeverityDraft() },
                              );
                            }}
                          >
                            +
                          </button>
                        </div>
                        {formOpen ? (
                          <form
                            className="complidesk-add-member-form"
                            onSubmit={(event) => {
                              event.preventDefault();
                              void addSeverityMember(groupId);
                            }}
                          >
                            <label>
                              <span>{spanish ? 'Nombre' : 'Name'}</span>
                              <input
                                name="name"
                                type="text"
                                autoComplete="name"
                                placeholder={spanish ? 'Opcional' : 'Optional'}
                                value={draft.name}
                                onChange={(event) =>
                                  setSeverityDrafts((prev) => ({
                                    ...prev,
                                    [groupId]: { ...draft, name: event.target.value },
                                  }))
                                }
                              />
                            </label>
                            <label>
                              <span>
                                {spanish
                                  ? group.sms
                                    ? 'Correo (o déjelo vacío solo para SMS)'
                                    : 'Correo'
                                  : group.sms
                                    ? 'Email (or leave blank for SMS only)'
                                    : 'Email'}
                              </span>
                              <input
                                name="email"
                                type="email"
                                autoComplete="email"
                                placeholder="you@company.com"
                                value={draft.email}
                                onChange={(event) =>
                                  setSeverityDrafts((prev) => ({
                                    ...prev,
                                    [groupId]: { ...draft, email: event.target.value },
                                  }))
                                }
                              />
                            </label>
                            <label>
                              <span>
                                {spanish
                                  ? group.sms
                                    ? 'Teléfono (SMS — 10 dígitos EE. UU. ok)'
                                    : 'Teléfono (opcional)'
                                  : group.sms
                                    ? 'Phone (SMS — 10-digit US ok)'
                                    : 'Phone (optional)'}
                              </span>
                              <input
                                name="phone"
                                type="tel"
                                autoComplete="tel"
                                inputMode="tel"
                                placeholder="+12105551234"
                                value={draft.phone}
                                onChange={(event) =>
                                  setSeverityDrafts((prev) => ({
                                    ...prev,
                                    [groupId]: { ...draft, phone: event.target.value },
                                  }))
                                }
                              />
                            </label>
                            <p className="complidesk-bff-meta">
                              {group.sms
                                ? spanish
                                  ? 'Correo o teléfono basta — use uno o ambos. Móviles de EE. UU. pueden ser 10 dígitos; los guardamos como +1… para SMS.'
                                  : 'Email or phone is enough — use either or both. US mobiles can be 10 digits; we store them as +1… for SMS.'
                                : spanish
                                  ? 'El correo basta para este grupo. El teléfono es opcional.'
                                  : 'Email is enough for this group. Phone is optional.'}
                            </p>
                            <button type="submit" className="dashboard-btn" disabled={severityBusy}>
                              {spanish ? 'Agregar al grupo' : 'Add to group'}
                            </button>
                          </form>
                        ) : null}
                      </div>
                    </section>
                  );
                })}
              </div>
              {severityNote ? (
                <p className="complidesk-bff-meta" role="status">
                  {severityNote}
                </p>
              ) : null}
            </section>

            <section className="complidesk-settings-block" aria-labelledby="complidesk-digest-heading">
              <h3 id="complidesk-digest-heading">{spanish ? 'Informe diario' : 'Daily report'}</h3>
              <p>
                {spanish
                  ? 'Una vez al día: resumen de buzones más tickets Standard/Low abiertos.'
                  : 'Once a day: mailbox review summary plus open Standard/Low tickets.'}
              </p>
              <div className="complidesk-settings-fields-row">
                <label className="complidesk-settings-field">
                  <span>{spanish ? 'Correos' : 'Emails'}</span>
                  <textarea
                    rows={3}
                    value={digestEmails}
                    onChange={(event) => setDigestEmails(event.target.value)}
                    placeholder={spanish ? 'una dirección por línea' : 'one address per line'}
                    autoComplete="email"
                  />
                </label>
                <label className="complidesk-settings-field">
                  <span>{spanish ? 'Mensajes de texto' : 'Text messages'}</span>
                  <textarea
                    rows={3}
                    value={digestPhones}
                    onChange={(event) => setDigestPhones(event.target.value)}
                    placeholder={spanish ? 'un teléfono por línea, p. ej. 2488181063' : 'one phone per line, e.g. 2488181063'}
                    inputMode="tel"
                  />
                </label>
              </div>
              <button type="button" className="dashboard-btn" disabled={digestBusy} onClick={() => void saveDailyReport()}>
                {digestBusy
                  ? spanish
                    ? 'Guardando…'
                    : 'Saving…'
                  : spanish
                    ? 'Guardar informe diario'
                    : 'Save daily report'}
              </button>
              <p className="complidesk-bff-meta" role="status">
                {digestNote ||
                  (settings?.dailyReport?.lastSentAt
                    ? spanish
                      ? `Último informe enviado ${settings.dailyReport.lastSentAt}.`
                      : `Last daily report sent ${settings.dailyReport.lastSentAt}.`
                    : spanish
                      ? 'Agregue contactos y guarde para recibir el digest.'
                      : 'Add emails or phones and save to get the digest.')}
              </p>
            </section>
            </div>

            <section className="complidesk-settings-block" aria-labelledby="complidesk-scan-heading">
              <h3 id="complidesk-scan-heading">{spanish ? 'Escaneo de intake del sitio' : 'Website intake scan'}</h3>
              <p>
                {spanish
                  ? 'Busca mailto, formularios, chat y enlaces de ayuda en el sitio público.'
                  : 'Search the public site for mailto addresses, web forms, chat widgets, and help links.'}
              </p>
              <label className="complidesk-settings-field">
                <span>{spanish ? 'Páginas a escanear (una URL por línea)' : 'Pages to scan (one URL per line)'}</span>
                <textarea
                  rows={4}
                  value={scanUrls}
                  onChange={(event) => setScanUrls(event.target.value)}
                  placeholder="https://nextcompli.com/contact"
                />
              </label>
              <button type="button" className="dashboard-btn" disabled={scanBusy} onClick={() => void runSiteDiscovery()}>
                {scanBusy
                  ? spanish
                    ? 'Escaneando…'
                    : 'Scanning…'
                  : spanish
                    ? 'Escanear sitio'
                    : 'Scan website'}
              </button>
              {scanNote ? (
                <p className="complidesk-bff-meta" role="status">
                  {scanNote}
                </p>
              ) : null}
            </section>

            <section className="complidesk-settings-block complidesk-settings-block--intake" aria-labelledby="complidesk-intake-heading">
              <h3 id="complidesk-intake-heading">{spanish ? 'Fuentes de intake' : 'Intake sources'}</h3>
              <p>
                {spanish
                  ? 'Marque los canales que pueden crear trabajo en el escritorio. Agrupados por tipo de canal (taxonomía ITIL del escritorio).'
                  : 'Check the channels that can create desk work. Grouped by channel kind (desk ITIL taxonomy).'}
              </p>
              <label className="complidesk-intake-schedule">
                <input
                  type="checkbox"
                  checked={mailTriageEnabled}
                  onChange={(event) => setMailTriageEnabled(event.target.checked)}
                />
                <span>
                  {spanish
                    ? 'Revisión diaria de buzones (pull) activada'
                    : 'Daily mailbox review (pull) enabled'}
                </span>
              </label>
              <div className="complidesk-settings-fields-row complidesk-mail-scan-fields">
                <label className="complidesk-settings-field">
                  <span>
                    <GlossaryTerm
                      term={spanish ? 'Qué escanear' : 'What to scan'}
                      definition={
                        spanish
                          ? 'No leídos = solo no leídos. Recientes = ventana de días. Backlog = no leídos históricos. Todos los recientes = leídos y no leídos en la ventana.'
                          : 'Unread = only unread. Recent = time window. Backlog = historical unread. All recent = read and unread in the window.'
                      }
                    >
                      {spanish ? 'Qué escanear' : 'What to scan'}
                    </GlossaryTerm>
                  </span>
                  <select
                    value={scanMode}
                    onChange={(event) => setScanMode(event.target.value as MailScanSettings['mode'])}
                  >
                    <option value="unread">{spanish ? 'Solo no leídos (ventana)' : 'Unread only (window)'}</option>
                    <option value="newer">{spanish ? 'Todos los recientes (ventana)' : 'All recent (window)'}</option>
                    <option value="unread_backlog">{spanish ? 'Backlog de no leídos' : 'Unread backlog'}</option>
                    <option value="all_recent">{spanish ? 'Todos los recientes' : 'All recent'}</option>
                  </select>
                </label>
                <label className="complidesk-settings-field">
                  <span>{spanish ? 'Días hacia atrás' : 'Lookback days'}</span>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={scanLookbackDays}
                    onChange={(event) => setScanLookbackDays(Number(event.target.value) || 14)}
                  />
                </label>
                <label className="complidesk-settings-field">
                  <span>
                    <GlossaryTerm
                      term={spanish ? 'Marcar leído' : 'Mark as read'}
                      definition={
                        spanish
                          ? 'Después de clasificar: nada, solo ruido, o todo lo parseado. Requiere permiso gmail.modify en la cuenta Workspace.'
                          : 'After classify: nothing, noise only, or every parsed message. Needs gmail.modify on the Workspace account.'
                      }
                    >
                      {spanish ? 'Marcar leído' : 'Mark as read'}
                    </GlossaryTerm>
                  </span>
                  <select
                    value={scanMarkRead}
                    onChange={(event) => setScanMarkRead(event.target.value as MailScanSettings['markRead'])}
                  >
                    <option value="none">{spanish ? 'No marcar' : 'Do not mark'}</option>
                    <option value="noise">{spanish ? 'Solo ruido' : 'Noise only'}</option>
                    <option value="all">{spanish ? 'Todo lo parseado' : 'All parsed'}</option>
                  </select>
                </label>
              </div>
              <div className="complidesk-settings-fields-row">
                <label className="complidesk-settings-field">
                  <span>
                    {spanish
                      ? 'Resumen urgente — correos'
                      : 'Important summary — emails'}
                  </span>
                  <textarea
                    rows={2}
                    value={importantEmails}
                    onChange={(event) => setImportantEmails(event.target.value)}
                    placeholder={spanish ? 'una dirección por línea' : 'one address per line'}
                  />
                </label>
                <label className="complidesk-settings-field">
                  <span>
                    {spanish
                      ? 'Resumen urgente — SMS'
                      : 'Important summary — text'}
                  </span>
                  <textarea
                    rows={2}
                    value={importantPhones}
                    onChange={(event) => setImportantPhones(event.target.value)}
                    placeholder={spanish ? 'un teléfono por línea' : 'one phone per line'}
                    inputMode="tel"
                  />
                </label>
              </div>
              <p className="complidesk-bff-meta">
                {spanish
                  ? 'El resumen urgente avisa cuando la triage marca correo como “Needs a person” o un incidente grave. El informe diario (arriba) cubre el resto.'
                  : 'Important summary pages when triage marks mail as Needs a person or a severe incident. The Daily report above covers the rest.'}
              </p>
              <div className="complidesk-settings-fields-row">
                <label className="complidesk-settings-field">
                  <span>{spanish ? 'Agregar buzón a parsear' : 'Add mailbox to parse'}</span>
                  <input
                    type="email"
                    value={customMailbox}
                    onChange={(event) => setCustomMailbox(event.target.value)}
                    placeholder="andrew.goss@gosslife.biz"
                    autoComplete="email"
                  />
                </label>
                <label className="complidesk-settings-field">
                  <span>{spanish ? 'Nombre (opcional)' : 'Label (optional)'}</span>
                  <input
                    type="text"
                    value={customMailboxLabel}
                    onChange={(event) => setCustomMailboxLabel(event.target.value)}
                    placeholder={spanish ? 'Andrew Workspace' : 'Andrew Workspace'}
                  />
                </label>
                <label className="complidesk-settings-field">
                  <span>
                    <GlossaryTerm
                      term={spanish ? 'Etiqueta Gmail' : 'Gmail label'}
                      definition={
                        spanish
                          ? 'Etiqueta anidada en Gmail, p. ej. jfgoss-andrew. La triage también lee correo con esa etiqueta.'
                          : 'Nested Gmail label, e.g. jfgoss-andrew. Triage also reads mail with that label.'
                      }
                    >
                      {spanish ? 'Etiqueta Gmail (opcional)' : 'Gmail label (optional)'}
                    </GlossaryTerm>
                  </span>
                  <input
                    type="text"
                    value={customMailboxGmailLabel}
                    onChange={(event) => setCustomMailboxGmailLabel(event.target.value)}
                    placeholder="jfgoss-andrew"
                  />
                </label>
              </div>
              {intakeGroups.length === 0 ? (
                <p className="complidesk-bff-empty">
                  {spanish ? 'No hay fuentes definidas para este cliente.' : 'No intake sources are defined for this desk client.'}
                </p>
              ) : (
                <div className="complidesk-intake-kinds" aria-label={spanish ? 'Fuentes de intake' : 'Intake sources'}>
                  {intakeGroups.map((group) => {
                    const kindMeta = intakeKinds.find((row) => row.id === group.kind);
                    const delivery = kindMeta?.delivery || group.rows[0]?.delivery;
                    const kindTitle = intakeKindLabel(group.kind, kindMeta, spanish);
                    const kindHelp = kindMeta?.summary || intakeKindHelp(group.kind, spanish);
                    return (
                      <section key={group.kind} className="complidesk-intake-kind">
                        <header className="complidesk-intake-kind-head">
                          <h4>
                            <GlossaryTerm term={kindTitle} definition={kindHelp}>
                              {kindTitle}
                            </GlossaryTerm>
                          </h4>
                          {delivery ? (
                            <span className={`complidesk-intake-delivery complidesk-intake-delivery--${delivery}`}>
                              {intakeDeliveryLabel(delivery, spanish)}
                            </span>
                          ) : null}
                        </header>
                        <p className="complidesk-bff-meta">{kindHelp}</p>
                        <ul className="complidesk-intake-source-list">
                          {group.rows.map((source, index) => {
                            const sourceId = String(source.id || `${group.kind}-${index}`);
                            const identity = source.address || source.source || source.ci || '';
                            const detail =
                              source.detail && source.detail !== identity ? source.detail : '';
                            return (
                              <li key={sourceId}>
                                <label className="complidesk-intake-source-row">
                                  <input
                                    type="checkbox"
                                    checked={selectedSourceIds.has(sourceId)}
                                    onChange={(event) => toggleIntakeSource(sourceId, event.target.checked)}
                                  />
                                  <span className="complidesk-intake-source-copy">
                                    <strong>{source.label || identity || sourceId}</strong>
                                    {[identity, detail].filter(Boolean).length > 0 ? (
                                      <span className="complidesk-bff-meta">
                                        {[identity, detail].filter(Boolean).join(' — ')}
                                      </span>
                                    ) : null}
                                  </span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      </section>
                    );
                  })}
                </div>
              )}
              <div className="complidesk-intake-actions">
                <button type="button" className="dashboard-btn" disabled={intakeBusy} onClick={() => void saveIntakeSources()}>
                  {intakeBusy
                    ? spanish
                      ? 'Guardando…'
                      : 'Saving…'
                    : spanish
                      ? 'Guardar fuentes de intake'
                      : 'Save intake sources'}
                </button>
                <button type="button" className="dashboard-btn" disabled={triageBusy} onClick={() => void runMailTriage()}>
                  {triageBusy
                    ? spanish
                      ? 'Ejecutando…'
                      : 'Running…'
                    : spanish
                      ? 'Ejecutar triage de correo'
                      : 'Run mail triage'}
                </button>
              </div>
              {intakeNote ? <p className="complidesk-host-loading">{intakeNote}</p> : null}
              {triageNote ? <p className="complidesk-host-loading">{triageNote}</p> : null}
            </section>
          </div>
        )
      ) : null}

      {!board && !error ? (
        <p className="complidesk-host-loading" aria-live="polite">
          {spanish ? 'Cargando el escritorio…' : 'Loading the desk…'}
        </p>
      ) : null}
    </section>
  );
};

export default CompliDeskWorkspace;
